import { Form } from 'antd';
import dayjs from 'dayjs';
import type { NamePath } from 'rc-field-form/es/interface';
import React, { useMemo } from 'react';

import { TableCell } from '../../TableCell/TableCell';

function toArray<T>(value?: T | T[] | null): T[] {
  if (value === undefined || value === null) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
}

function get(
  entity: unknown,
  path: (string | number)[] | readonly (string | number)[]
): unknown {
  return [...path].reduce<unknown>((current, key) => {
    if (current === null || current === undefined) {
      return undefined;
    }

    return (current as Record<string | number, unknown>)[key];
  }, entity);
}

type PureFieldCellProps<U> = {
  name: NamePath;
  children: (value?: U) => React.ReactNode;
  dateKey: string;
};

const PureFieldCellComponent = <U,>({
  name,
  children,
  dateKey,
}: PureFieldCellProps<U>) => {
  const isMonday = dayjs(dateKey).day() === 1;
  const shouldUpdate = useMemo(
    () => (prev: unknown, next: unknown) => {
      const namePath = toArray(name);
      const prevVal = get(prev, namePath);
      const nextVal = get(next, namePath);

      return prevVal !== nextVal;
    },
    [name]
  );

  return (
    <TableCell key={dateKey} isMonday={isMonday}>
      <Form.Item noStyle shouldUpdate={shouldUpdate}>
        {(form) => children(form.getFieldValue(name) as U | undefined)}
      </Form.Item>
    </TableCell>
  );
};

export const PureFieldCell = React.memo(PureFieldCellComponent) as unknown as <
  T
>(
  props: PureFieldCellProps<T>
) => JSX.Element;
